import {
  boolCheckboxSpec,
  group,
  numberInputSpec,
  repeatable,
  stringInputSpec,
  stringSelectSpec,
} from '../../../lib/editing-configs';
import UniversalText from '../../universal-text/universal-text';
import UniversalButton from '../../universal-button/universal-button';
import YouTubePlayer from '../../../lib/youtube-player/youtube-player';
import Mp4VideoPlayer from '../../../lib/mp4-video-player/mp4-video-player';
import SpotifyPlayer from '../../../lib/spotify-player';
import SoundCloudPlayer from '../../../lib/soundcloud-player';
import UniversalAccordion from '../../universal-accordion/universal-accordion';
import UniversalImage from '../../universal-image/universal-image';
import BainReferralBannerSC from '../../bain-referral-banner-sc/bain-referral-banner-sc';
import AdvertiserDisclosureWrapper from '../../advertiser-disclosure/advertiser-disclosure';
import LottieAnimation from '../../lottie-animation/lottie-animation';
import UniversalContainer from '../../universal-container';
import CurrentDate from '../../current-date/current-date';
import ThirdPartyEmbedV2 from '../../third-party-embed-v2/third-party-embed-v2';
import { componentTypes } from './component-types';

export default (componentDoc) => {
  // make sure all gridItems has '$unique_key'
  const filteredGridItems = componentDoc?.props?.gridItems?.filter(
    (gridItem) => '$unique_key' in gridItem
  );

  const componentDocument = {
    ...componentDoc,
    // update componentDocument with sanitized gridItems
    props: { ...componentDoc?.props, gridItems: filteredGridItems },
  };

  const centeredInParentBackgroundInputConfig = componentDocument?.props
    ?.centeredInParent
    ? [
        ...UniversalContainer.getEditingConfig(
          componentDocument,
          'Centered Parent Background',
          ['backgroundColor', 'backgroundImage'],
          'centeredInParentBackground',
          {
            disableGroup: true,
            customLabels: {
              background: 'Centered In Parent Background',
            },
          }
        ),
      ]
    : [];

  const floatingGridControls = componentDocument?.props?.floatingGrid
    ? [
        group('Floating Grid Options', [
          stringInputSpec('floatTop', 'Float Top', {
            help: 'Defaults to "0"',
          }),
          stringInputSpec('floatBottom', 'Float Bottom', {
            help: 'Defaults to "0"',
          }),
          stringInputSpec('floatLeft', ' Float Left', {
            help: 'Defaults to "0"',
          }),
          stringInputSpec('floatRight', ' Float Right', {
            help: 'Defaults to "0"',
          }),
          numberInputSpec('floatIndex', 'Float Index'),
        ]),
      ]
    : [];

  return [
    boolCheckboxSpec(
      'disableDefaultProps',
      'Disable Grid Outline/Transparency?'
    ),
    stringSelectSpec(
      'columns',
      'Number of columns',
      [
        {
          value: '',
          label: 'Auto',
        },
        {
          value: '1',
          label: '1',
        },
        {
          value: '2',
          label: '2',
        },
        {
          value: '3',
          label: '3',
        },
        {
          value: '4',
          label: '4',
        },
      ],
      {
        help: 'Number of columns in layout on desktop',
      }
    ),

    boolCheckboxSpec('floatingGrid', 'Floating Grid?'),
    ...floatingGridControls,

    group('Grid Container', [
      numberInputSpec('gap', 'Gap', {
        help: 'Space between grid items in pixels. Defaults to "0" ',
      }),
      boolCheckboxSpec('centeredInParent', 'Centered In Parent'),
      boolCheckboxSpec('fitWidth', 'Fit Width?'),
      ...centeredInParentBackgroundInputConfig,
      ...UniversalContainer.getEditingConfig(
        componentDocument,
        'Grid Container',
        [
          'backgroundColor',
          'backgroundImage',
          'border',
          'margin',
          'padding',
          'hide',
          'parallax',
          'jumpLinkId',
        ],
        'container',
        {
          disableGroup: true,
        }
      ),
    ]),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Title',
      [
        'fontWeight',
        'groupButton',
        'width',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      'title'
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Subtitle',
      [
        'fontWeight',
        'groupButton',
        'width',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      'subtitle',
      { text: 'text' }
    ),
    repeatable('gridItems', '', 'Grid Item', (item) => [
      ...UniversalContainer.getEditingConfig(
        componentDocument,
        'Grid Item Container',
        [
          'backgroundColor',
          'backgroundImage',
          'border',
          'margin',
          'padding',
          'width',
          'alignment',
          'hide',
        ],
        `gridItems.${item}.itemContainer`
      ),
      repeatable('itemComponents', '', 'Component', (component) => {
        const componentType =
          componentDocument?.props?.gridItems?.[item]?.itemComponents?.[
            component
          ]?.type;

        const textInputConfig =
          componentType === componentTypes.TEXT
            ? [
                ...UniversalText.getEditingConfig(
                  componentDocument,
                  'Text Options',
                  [
                    'fontWeight',
                    'groupButton',
                    'width',
                    'margin',
                    'padding',
                    'fontFamily',
                    'textColor',
                    'textSize',
                    'textType',
                    'placement',
                  ],
                  `gridItems.${item}.itemComponents.${component}.text`,
                  { disableGroup: true }
                ),
              ]
            : [];

        const imageInputConfig =
          componentType === componentTypes.IMAGE
            ? [
                ...UniversalImage.getEditingConfig(
                  componentDocument,
                  'Image Options',
                  ['border', 'margin', 'padding'],
                  `gridItems.${item}.itemComponents.${component}.image`,
                  { disableGroup: true }
                ),
              ]
            : [];

        const youtubeInputConfig =
          componentType === componentTypes.YOUTUBE
            ? [
                ...YouTubePlayer.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.youtube`,
                  true
                ),
              ]
            : [];

        const mp4VideoInputConfig =
          componentType === componentTypes.MP4VIDEO
            ? [
                ...Mp4VideoPlayer.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.mp4Video`,
                  true
                ),
              ]
            : [];

        const spotifyInputConfig =
          componentType === componentTypes.SPOTIFY
            ? [
                ...SpotifyPlayer.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.spotify`,
                  true
                ),
              ]
            : [];

        const soundCloudInputConfig =
          componentType === componentTypes.SOUNDCLOUD
            ? [
                ...SoundCloudPlayer.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.soundcloud`,
                  true
                ),
              ]
            : [];

        const buttonInputConfig =
          componentType === componentTypes.BUTTON
            ? [
                ...UniversalButton.getEditingConfig(
                  componentDocument,
                  'Button Options',
                  [],
                  `gridItems.${item}.itemComponents.${component}.button`,
                  { disableGroup: true, includeContainer: true }
                ),
              ]
            : [];

        const bainReferralBannerConfig =
          componentType === componentTypes.BAINREFERRALBANNER
            ? [
                ...BainReferralBannerSC.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.bainReferralBanner`
                ),
              ]
            : [];
        const advertiserDisclosureConfig =
          componentType === componentTypes.ADVERTISERDISCLOSURE
            ? [
                ...AdvertiserDisclosureWrapper.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.advertiserDisclosure`
                ),
              ]
            : [];
        const accordionConfig =
          componentType === componentTypes.ACCORDION
            ? [
                ...UniversalAccordion.getEditingConfig(
                  componentDocument,
                  'Accordion',
                  `gridItems.${item}.itemComponents.${component}.accordion`,
                  { disableGroup: true }
                ),
              ]
            : [];

        const lottieConfig =
          componentType === componentTypes.LOTTIE
            ? [
                ...LottieAnimation.getEditingConfig(
                  componentDocument,
                  'Lottie Animation',
                  `gridItems.${item}.itemComponents.${component}.lottie`,
                  { disableGroup: true }
                ),
              ]
            : [];

        const currentDateConfig =
          componentType === componentTypes.CURRENTDATE
            ? [
                ...CurrentDate.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.currentDate`
                ),
              ]
            : [];

        const thirdPartyEmbedConfig =
          componentType === componentTypes.THIRDPARTYEMBED
            ? [
                ...ThirdPartyEmbedV2.getEditingConfig(
                  componentDocument,
                  `gridItems.${item}.itemComponents.${component}.thirdPartyEmbed`
                ),
              ]
            : [];

        return [
          !componentType
            ? stringSelectSpec('type', 'Component List', [
                { label: 'Select component', value: 'none' },
                {
                  label: 'Advertiser Disclosure',
                  value: componentTypes.ADVERTISERDISCLOSURE,
                },
                { label: 'Accordion', value: componentTypes.ACCORDION },
                {
                  label: 'Bain referral banner',
                  value: componentTypes.BAINREFERRALBANNER,
                },
                { label: 'Button', value: componentTypes.BUTTON },
                { label: 'Current Date', value: componentTypes.CURRENTDATE },
                { label: 'Image', value: componentTypes.IMAGE },
                { label: 'Lottie Animation', value: componentTypes.LOTTIE },
                { label: 'MP4 Video', value: componentTypes.MP4VIDEO },
                { label: 'SoundCloud', value: componentTypes.SOUNDCLOUD },
                { label: 'Spotify', value: componentTypes.SPOTIFY },
                { label: 'Text', value: componentTypes.TEXT },
                { label: 'YouTube', value: componentTypes.YOUTUBE },
                {
                  label: 'Third Party Embed',
                  value: componentTypes.THIRDPARTYEMBED,
                },
              ])
            : [],
          ...advertiserDisclosureConfig,
          ...accordionConfig,
          ...bainReferralBannerConfig,
          ...buttonInputConfig,
          ...imageInputConfig,
          ...lottieConfig,
          ...mp4VideoInputConfig,
          ...soundCloudInputConfig,
          ...spotifyInputConfig,
          ...textInputConfig,
          ...youtubeInputConfig,
          ...currentDateConfig,
          ...thirdPartyEmbedConfig,
        ];
      }),
    ]),
  ];
};
